import { Link } from 'react-router-dom'
import { ArrowRight, Users, Target, Award, Heart } from 'lucide-react'
import { Button } from '@/components/ui'

const values = [
  {
    icon: Target,
    title: "Transparency",
    description:
      "Every vehicle comes with a detailed history report and inspection.",
  },
  {
    icon: Award,
    title: "Quality",
    description:
      "Only the best vehicles make it through our rigorous selection process.",
  },
  {
    icon: Heart,
    title: "Customer First",
    description:
      "Your satisfaction is our top priority, backed by our 7-day return policy.",
  },
  {
    icon: Users,
    title: "Trust",
    description:
      "Building lasting relationships through honest, fair dealings.",
  },
];

export function AboutPage() {
  return (
    <div>
      {/* Hero */}
      <section
        className="relative min-h-screen flex items-center justify-center overflow-hidden bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('/img/heroes/home-hero-hero.webp')` }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black/35" />

        {/* Content - centered in viewport */}
        <div className="relative z-10 w-full flex flex-col items-center justify-center px-6">
          <div className="text-center max-w-4xl w-full">
            <h1 className="font-display text-5xl md:text-6xl text-white leading-tight tracking-tight">
              About Bespokian Auto
            </h1>
            <p className="mt-6 text-lg md:text-xl text-white/90 max-w-2xl mx-auto">
              We’re building a simpler way to buy a car—transparent pricing, quality inventory,
              and a customer-first experience from search to delivery.
            </p>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4 w-full">
            <Link to="/vehicles" className="w-full sm:w-auto">
              <Button size="lg" className="bg-white text-navy-900 hover:bg-white/90 w-full">
                Browse Vehicles
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link to="/financing" className="w-full sm:w-auto">
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white/10 w-full"
              >
                Get Pre-Approved
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Mission */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900">Our Mission</h2>
              <p className="mt-4 text-lg text-gray-600">
                At Bespokian Auto, we believe buying a car should be an exciting
                experience, not a stressful one. We've reimagined the entire
                process from the ground up, leveraging technology to create a
                transparent, convenient, and customer-first approach to
                automotive retail.
              </p>
              <p className="mt-4 text-lg text-gray-600">
                Whether you're browsing our inventory online or taking a test
                drive, our team is here to ensure you find the perfect vehicle
                for your needs.
              </p>
            </div>
            <div className="bg-gray-100 rounded-2xl p-8">
              <img
                src="https://placehold.co/600x400/e2e8f0/475569?text=Our+Story"
                alt="Our Story"
                className="rounded-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900">Our Values</h2>
            <p className="mt-4 text-lg text-gray-600 max-w-2xl mx-auto">
              These core values guide everything we do at Bespokian Auto.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value) => (
              <div
                key={value.title}
                className="bg-white rounded-xl p-6 shadow-sm border border-gray-200"
              >
                <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center mb-4">
                  <value.icon className="h-6 w-6 text-primary-600" />
                </div>
                <h3 className="font-semibold text-gray-900">{value.title}</h3>
                <p className="mt-2 text-gray-600 text-sm">
                  {value.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-4xl font-bold text-primary-600">10,000+</div>
              <div className="mt-2 text-gray-600">Vehicles Sold</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">98%</div>
              <div className="mt-2 text-gray-600">Customer Satisfaction</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">50</div>
              <div className="mt-2 text-gray-600">States Served</div>
            </div>
            <div>
              <div className="text-4xl font-bold text-primary-600">4.9</div>
              <div className="mt-2 text-gray-600">Average Rating</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
